import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnChanges,
  OnInit,
  ViewChild,
  inject,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { FeaturePurePipe } from '@wilson/authorization';
import { FeatureFlagPurePipe, FeatureFlagsModule } from '@wilson/feature-flags';
import {
  ActivitiesAssignment,
  FeatureName,
  QualificationName,
  ShiftAssignment,
  ShiftAssignmentTags,
  ShiftValidationDetails,
  UserTimelinesModal,
} from '@wilson/interfaces';
import { UserTimelinesProfitabilityScoreComponent } from '@wilson/profitability-score/core';
import { ProfitabilityScoreState } from '@wilson/profitability-score/state';
import {
  SharedComponentsModule,
  UserDetailsModule,
} from '@wilson/shared-components';
import { OvertimeDataSourceService } from '@wilson/shift-timeline/assignments-gantt-chart';
import { ModalInteractionService } from '@wilson/shift-timeline/core';
import {
  ShiftAssignmentModalState,
  ShiftTimelineInteractionsState,
} from '@wilson/shift-timeline/state';
import { ShiftValidationsCoreModule } from '@wilson/shift-validations/core';
import {
  ScoreMap,
  ShiftScoresService,
  SmartScoreMap,
} from '@wilson/shift/core';
import { LocalizationState } from '@wilson/localization';
import { ShiftValidationErrorService } from '@wilson/shifts';
import { OvertimeValueComponent } from '@wilson/work-time-management';
import { NzRadioModule } from 'ng-zorro-antd/radio';
import { NzSkeletonModule } from 'ng-zorro-antd/skeleton';
import { NzTooltipBaseDirective, NzTooltipModule } from 'ng-zorro-antd/tooltip';
import { combineLatest, map, Observable } from 'rxjs';
import { UserTimelineInfoQualificationsComponent } from '../user-timeline-info-qualifications/user-timeline-info-qualifications.component';
import { UserTimelineInfoScoresComponent } from '../user-timeline-info-scores/user-timeline-info-scores.component';
import { UserTimelineTagsComponent } from '../user-timeline-tags/user-timeline-tags.component';

@Component({
  selector: 'wilson-user-timeline-info-radio-group',
  templateUrl: './user-timeline-info-radio-group.component.html',
  styleUrls: ['./user-timeline-info-radio-group.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    TranslateModule,
    FormsModule,
    NzRadioModule,
    UserTimelinesProfitabilityScoreComponent,
    NzSkeletonModule,
    OvertimeValueComponent,
    NzTooltipModule,
    SharedComponentsModule,
    ShiftValidationsCoreModule,
    UserDetailsModule,
    UserTimelineTagsComponent,
    FeatureFlagsModule,
    UserTimelineInfoQualificationsComponent,
    UserTimelineInfoScoresComponent,
  ],
})
export class UserTimelineInfoRadioGroupComponent implements OnInit, OnChanges {
  private readonly featureFlagPurePipe = inject(FeatureFlagPurePipe);
  private readonly featurePurePipe = inject(FeaturePurePipe);
  private readonly store = inject(Store);
  private readonly shiftScoresService = inject(ShiftScoresService);
  private readonly shiftValidationErrorService = inject(
    ShiftValidationErrorService,
  );
  private overtimeDataSourceService = inject(OvertimeDataSourceService);
  modalInteractionService = inject(ModalInteractionService);

  @ViewChild('tooltip') tooltip!: NzTooltipBaseDirective;

  radioValue!: string;
  public smartScores$!: Observable<SmartScoreMap | null>;
  public prefScores$!: Observable<ScoreMap | null>;
  public shiftProfitabilityScore$!: Observable<number | null | undefined>;
  public tags$!: Observable<ShiftAssignmentTags[] | null>;

  @Input() user!: UserTimelinesModal;
  @Input() index!: number;

  userOvertime$: Observable<number | undefined> | undefined;
  readonly isLoadingUsersOvertimes$ =
    this.overtimeDataSourceService.isLoadingUsersOvertimesStream();
  readonly overtimeVisible$ = combineLatest([
    this.featureFlagPurePipe.transform('portal-find-staff-overtime'),
    this.featurePurePipe.transform(FeatureName.Payroll),
  ]).pipe(
    map(([featureFlag, feature]) => {
      return featureFlag && feature;
    }),
  );

  smartScoreProgress!: string;
  prefScoreProgress!: string;
  hasFinishedLoadingScores$!: Observable<boolean>;
  isEnabledProfitabilityScores$ = this.store.select(
    ProfitabilityScoreState.enableSettings,
  );

  qualificationNames$!: Observable<QualificationName[]>;
  qualificationFulfilled$!: Observable<boolean | undefined>;
  userAssignment$!: Observable<
    ShiftAssignment | ActivitiesAssignment | undefined
  >;
  workingTimeValidations$!: Observable<ShiftValidationDetails[] | undefined>;
  professionValidations$!: Observable<ShiftValidationDetails[] | undefined>;
  shiftLogicValidations$!: Observable<ShiftValidationDetails[] | undefined>;
  public showRadioButton$!: Observable<boolean>;

  ngOnInit(): void {
    this.hasFinishedLoadingScores$ = this.store.select(
      ShiftAssignmentModalState.isFetchedShiftAssignmentSuccessful,
    );

    this.showRadioButton$ = this.store.select(
      ShiftTimelineInteractionsState.showRadioButton,
    );
  }

  ngOnChanges(): void {
    this.initOvertimes();
    this.calculateUserScoresAndQualification();
  }

  initOvertimes(): void {
    const userId = this.user.id;

    if (userId) {
      this.userOvertime$ =
        this.overtimeDataSourceService.userOvertimeMinutesStream(userId);
    }
  }

  calculateUserScoresAndQualification(): void {
    if (this.user?.id) {
      this.userAssignment$ = this.store.select(
        ShiftAssignmentModalState.userShiftAssignment(this.user.id),
      );

      this.tags$ = this.userAssignment$.pipe(
        map((assignment) => {
          const activityAssignment = assignment as ActivitiesAssignment;
          if (activityAssignment?.tags) {
            return activityAssignment.tags;
          } else {
            return null;
          }
        }),
      );

      this.qualificationNames$ = combineLatest([
        this.userAssignment$,
        this.featureFlagPurePipe.transform('quick-check-qualification-v2'),
        this.store.select(LocalizationState.getEntityLang),
      ]).pipe(
        map(
          ([
            assignment,
            quickCheckQualificationV2FeatureFlagEnabled,
            entityLang,
          ]) => {
            if (assignment) {
              return quickCheckQualificationV2FeatureFlagEnabled
                ? this.shiftValidationErrorService.getQualificationV2Name(
                    assignment.shiftValidationErrors,
                    entityLang,
                  )
                : this.shiftValidationErrorService.getQualificationName(
                    assignment.shiftValidationErrors,
                  );
            }

            return [];
          },
        ),
      );

      this.smartScores$ = this.userAssignment$.pipe(
        map((assignment) =>
          assignment?.scores
            ? this.shiftScoresService.convertToMappedSmartScore(
                assignment.scores,
              )
            : null,
        ),
      );

      this.prefScores$ = this.userAssignment$.pipe(
        map((assignment) =>
          assignment?.scores
            ? this.shiftScoresService.convertToPrefScoreMap(assignment.scores)
            : null,
        ),
      );

      this.shiftProfitabilityScore$ = combineLatest([
        this.userAssignment$,
        this.isEnabledProfitabilityScores$,
      ]).pipe(
        map(([assignment, isEnabled]) =>
          isEnabled && assignment?.scores
            ? this.shiftScoresService.getScoreForProfitabilityScore(
                assignment.scores,
              )
            : null,
        ),
      );

      const isUserApprovalEnabled$ = this.featureFlagPurePipe.transform(
        'portal-shift-validation-user-approval',
      );

      this.workingTimeValidations$ = combineLatest([
        this.userAssignment$,
        isUserApprovalEnabled$,
      ]).pipe(
        map(([userValidation, isUserApprovalEnabled]) =>
          userValidation
            ? ShiftValidationErrorService.getShiftWorkingTimeValidation(
                userValidation.shiftValidationErrors,
                isUserApprovalEnabled,
              )
            : [],
        ),
      );

      const isUserProfessionEnabled$ = this.featureFlagPurePipe.transform(
        'portal-shift-validation-user-professions',
      );

      this.professionValidations$ = combineLatest([
        this.userAssignment$,
        isUserProfessionEnabled$,
      ]).pipe(
        map(([userValidation, isUserProfessionEnabled]) =>
          userValidation
            ? this.shiftValidationErrorService.getShiftProfessionValidation(
                userValidation.shiftValidationErrors,
                isUserProfessionEnabled,
              )
            : [],
        ),
      );

      this.qualificationFulfilled$ = combineLatest([
        this.workingTimeValidations$.pipe(
          map((validationDetails) =>
            validationDetails?.every((item) => item.valid),
          ),
        ),
        this.qualificationNames$.pipe(
          map((validationDetails) =>
            validationDetails?.every((item) => item.valid),
          ),
        ),
      ]).pipe(
        map(
          ([workingTimeValidations, qualificationNames]) =>
            workingTimeValidations && qualificationNames,
        ),
      );

      this.shiftLogicValidations$ = this.userAssignment$.pipe(
        map((assignment) =>
          assignment?.shiftValidationErrors
            ? this.shiftValidationErrorService.getShiftLogicValidation(
                assignment.shiftValidationErrors,
              )
            : [],
        ),
      );
    }
  }

  onTooltipExpanded(): void {
    this.tooltip.updatePosition();
  }
}
